import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BatchType, BatchTypePackage, PackagingType, UpdateBatchTypePackageRequest } from '../../../../types/batchPickingSetup';
import { batchPickingSetupService } from '../../../../services/batchPickingSetupService';
import { NumericInput } from '../../../common/inputs';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface BatchTypePackagesModalProps {
  batchType: BatchType;
  batchTypes: BatchType[];
  packagingTypes: PackagingType[];
  onClose: () => void;
}

const formatNumber = (value: number) => Number(value.toFixed(3)).toString();

/** Eskens mål på én linje, f.eks. «40×30×20 · vol 24 · maks 15». */
export const compactDimensions = (p: BatchTypePackage, volLabel: string, maxLabel: string) =>
  `${formatNumber(p.height)}×${formatNumber(p.width)}×${formatNumber(p.length)} · ${volLabel} ${formatNumber(p.volume)} · ${maxLabel} ${formatNumber(p.maxWeight)}`;

/** Sant når en eske har mindre volum enn esken over (motoren tar første som passer). */
export const isNotAscending = (packages: BatchTypePackage[]) =>
  packages.some((p, i) => i > 0 && p.volume < packages[i - 1].volume);

// Felles kolonnebredder for overskrift og rader
const GRID = 'grid grid-cols-[24px_80px_minmax(0,1fr)_minmax(0,1.4fr)_80px_32px] items-center gap-3';
const fieldClass = 'w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';

interface SortableRowProps {
  item: BatchTypePackage;
  dimensions: string;
  batchTypes: BatchType[];
  packagingOptions: PackagingType[];
  disabled: boolean;
  onSave: (item: BatchTypePackage, changes: Partial<UpdateBatchTypePackageRequest>) => void;
  onRemove: (item: BatchTypePackage) => void;
}

const SortablePackageRow: React.FC<SortableRowProps> = ({ item, dimensions, batchTypes, packagingOptions, disabled, onSave, onRemove }) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.packagingTypeNo });
  const [quantity, setQuantity] = useState(item.numOfPackages != null ? String(item.numOfPackages) : '');
  const [sortId, setSortId] = useState(String(item.sortID));

  useEffect(() => {
    setQuantity(item.numOfPackages != null ? String(item.numOfPackages) : '');
  }, [item.numOfPackages]);

  useEffect(() => {
    setSortId(String(item.sortID));
  }, [item.sortID]);

  const commitQuantity = () => {
    const next = quantity.trim() === '' ? null : parseInt(quantity, 10);
    if (next !== item.numOfPackages) onSave(item, { numOfPackages: next });
  };

  const commitSortId = () => {
    const next = sortId.trim() === '' ? 0 : parseInt(sortId, 10);
    if (next !== item.sortID) onSave(item, { sortID: next });
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div ref={setNodeRef} style={style} className={`${GRID} px-3 py-2 bg-white border border-gray-200 rounded-md`}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
        title={t('manager.modules.batchPickingSetup.packages.drag', {}, 'Dra for å endre rekkefølge')}
        disabled={disabled}
      >
        <GripVertical className="w-5 h-5" />
      </button>
      <NumericInput
        mode="integer"
        value={sortId}
        onChange={(e) => setSortId(e.target.value)}
        onBlur={commitSortId}
        onKeyDown={blurOnEnter}
        disabled={disabled}
        className={`${fieldClass} text-right`}
        aria-label={t('manager.modules.batchPickingSetup.fields.sortId', {}, 'Sortering')}
      />
      <select
        value={item.pListBatchTypeNo}
        onChange={(e) => onSave(item, { pListBatchTypeNo: parseInt(e.target.value, 10) })}
        disabled={disabled}
        className={fieldClass}
        aria-label={t('manager.modules.batchPickingSetup.fields.batchType', {}, 'Samplukk-type')}
      >
        {batchTypes.map((bt) => (
          <option key={bt.pListBatchTypeNo} value={bt.pListBatchTypeNo}>
            {bt.pListBatchTypeNo} – {bt.description}
          </option>
        ))}
      </select>
      <div className="min-w-0">
        <select
          value={item.packagingTypeNo}
          onChange={(e) => onSave(item, { packagingTypeNo: parseInt(e.target.value, 10) })}
          disabled={disabled}
          className={fieldClass}
          aria-label={t('manager.modules.batchPickingSetup.fields.packagingType', {}, 'Eske')}
        >
          {packagingOptions.map((pt) => (
            <option key={pt.packagingTypeNo} value={pt.packagingTypeNo}>
              {pt.packagingTypeNo} – {pt.description}
            </option>
          ))}
        </select>
        <div className="text-xs text-gray-600 mt-0.5 truncate">{dimensions}</div>
      </div>
      <NumericInput
        mode="integer"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        onBlur={commitQuantity}
        onKeyDown={blurOnEnter}
        disabled={disabled}
        className={`${fieldClass} text-right`}
        aria-label={t('manager.modules.batchPickingSetup.packages.quantity', {}, 'Antall')}
      />
      <button
        type="button"
        onClick={() => onRemove(item)}
        disabled={disabled}
        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
        title={t('manager.modules.batchPickingSetup.packages.remove', {}, 'Fjern esken fra samplukket')}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

/**
 * Esker i en samplukk-type (PListBatchTypePackages). Alle feltene på koblingen kan endres:
 * samplukk-type (flytter esken), eske, antall og sortering. Regelmotoren prøver eskene i
 * SortID-rekkefølge og velger den første som passer — derfor varsel når volumet ikke stiger.
 */
export const BatchTypePackagesModal: React.FC<BatchTypePackagesModalProps> = ({ batchType, batchTypes, packagingTypes, onClose }) => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<BatchTypePackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addPackagingTypeNo, setAddPackagingTypeNo] = useState('');

  const volLabel = t('manager.modules.batchPickingSetup.packages.volShort', {}, 'vol');
  const maxLabel = t('manager.modules.batchPickingSetup.packages.maxShort', {}, 'maks');

  const loadPackages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPackages(await batchPickingSetupService.getBatchTypePackages(batchType.pListBatchTypeNo));
    } catch (err) {
      console.error('Error loading batch type packages:', err);
      setError(t('manager.modules.batchPickingSetup.packages.errors.loadFailed', {}, 'Feil ved lasting av esker'));
    } finally {
      setLoading(false);
    }
  }, [batchType.pListBatchTypeNo, t]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const runAction = useCallback(async (action: () => Promise<void>) => {
    setSaving(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      console.error('Error saving batch type packages:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.batchPickingSetup.packages.errors.saveFailed', {}, 'Feil ved lagring'));
      await loadPackages();
    } finally {
      setSaving(false);
    }
  }, [loadPackages, t]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = packages.findIndex((p) => p.packagingTypeNo === active.id);
    const newIndex = packages.findIndex((p) => p.packagingTypeNo === over.id);
    const reordered = arrayMove(packages, oldIndex, newIndex);
    setPackages(reordered);
    runAction(async () => {
      setPackages(await batchPickingSetupService.reorderBatchTypePackages(
        batchType.pListBatchTypeNo, reordered.map((p) => p.packagingTypeNo)));
    });
  };

  // Lagrer hele raden med endringen; listen hentes på nytt siden sortering, eske eller
  // type kan ha endret seg (en eske som flyttes til en annen type forsvinner herfra).
  const handleSave = (item: BatchTypePackage, changes: Partial<UpdateBatchTypePackageRequest>) => {
    runAction(async () => {
      await batchPickingSetupService.updateBatchTypePackage(item.pListBatchTypeNo, item.packagingTypeNo, {
        pListBatchTypeNo: item.pListBatchTypeNo,
        packagingTypeNo: item.packagingTypeNo,
        numOfPackages: item.numOfPackages,
        sortID: item.sortID,
        ...changes
      });
      setPackages(await batchPickingSetupService.getBatchTypePackages(batchType.pListBatchTypeNo));
    });
  };

  const handleRemove = (item: BatchTypePackage) => {
    const confirmed = window.confirm(t('manager.modules.batchPickingSetup.packages.confirmRemove', { '0': item.description ?? item.packagingTypeNo },
      `Fjerne «${item.description ?? item.packagingTypeNo}» fra samplukket? Esken blir liggende i eskelisten.`));
    if (!confirmed) return;
    runAction(async () => {
      await batchPickingSetupService.removeBatchTypePackage(batchType.pListBatchTypeNo, item.packagingTypeNo);
      setPackages((prev) => prev.filter((p) => p.packagingTypeNo !== item.packagingTypeNo));
    });
  };

  const handleAdd = () => {
    const no = parseInt(addPackagingTypeNo, 10);
    if (isNaN(no)) return;
    runAction(async () => {
      const added = await batchPickingSetupService.addBatchTypePackage(batchType.pListBatchTypeNo, no, null);
      setPackages((prev) => [...prev, added]);
      setAddPackagingTypeNo('');
    });
  };

  const usedPackagingNos = useMemo(() => new Set(packages.map((p) => p.packagingTypeNo)), [packages]);
  const availablePackagingTypes = useMemo(
    () => packagingTypes.filter((pt) => !usedPackagingNos.has(pt.packagingTypeNo)),
    [packagingTypes, usedPackagingNos]
  );
  // Eskevalg per rad: radens egen eske + esker som ikke allerede er i typen
  const packagingOptionsFor = (item: BatchTypePackage) =>
    packagingTypes.filter((pt) => pt.packagingTypeNo === item.packagingTypeNo || !usedPackagingNos.has(pt.packagingTypeNo));

  const notAscending = isNotAscending(packages);
  const typeOverridesQuantity = batchType.numOfPackages > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('manager.modules.batchPickingSetup.packages.title', {}, 'Esker i dette samplukket')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {batchType.pListBatchTypeNo} – {batchType.description}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl" aria-label={t('manager.modules.adminDelivery.common.closeModal', {}, 'Lukk modal')}>×</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <p className="text-sm text-gray-600">
            {t('manager.modules.batchPickingSetup.packages.help', {}, 'Eskene prøves ovenfra og ned, og den første esken som passer brukes. Legg minste eske øverst.')}
          </p>

          {typeOverridesQuantity && (
            <p className="text-sm text-gray-600">
              {t('manager.modules.batchPickingSetup.packages.typeOverrides', { '0': batchType.numOfPackages },
                `Samplukk-typen har Antall kunder = ${batchType.numOfPackages}. Det brukes i stedet for antallet per eske.`)}
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {notAscending && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-yellow-800 text-sm">
                {t('manager.modules.batchPickingSetup.packages.notAscending', {}, 'Rekkefølgen går ikke fra minst til størst volum. En større eske over en mindre gjør at den mindre sjelden blir valgt.')}
              </p>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">{t('common.status.loading', {}, 'Laster...')}</p>
          ) : packages.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t('manager.modules.batchPickingSetup.packages.empty', {}, 'Ingen esker er lagt til. Uten esker blir ingen plukklister samplukket med denne typen.')}
            </p>
          ) : (
            <div className="space-y-2">
              <div className={`${GRID} px-3 text-xs font-medium text-gray-600`}>
                <span />
                <span className="text-right">{t('manager.modules.batchPickingSetup.fields.sortId', {}, 'Sortering')}</span>
                <span>{t('manager.modules.batchPickingSetup.fields.batchType', {}, 'Samplukk-type')}</span>
                <span>{t('manager.modules.batchPickingSetup.fields.packagingType', {}, 'Eske')}</span>
                <span className="text-right">{t('manager.modules.batchPickingSetup.packages.quantity', {}, 'Antall')}</span>
                <span />
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={packages.map((p) => p.packagingTypeNo)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {packages.map((p) => (
                      <SortablePackageRow
                        key={p.packagingTypeNo}
                        item={p}
                        dimensions={compactDimensions(p, volLabel, maxLabel)}
                        batchTypes={batchTypes}
                        packagingOptions={packagingOptionsFor(p)}
                        disabled={saving}
                        onSave={handleSave}
                        onRemove={handleRemove}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          <div className="flex items-center gap-2 pt-4 border-t">
            <select
              value={addPackagingTypeNo}
              onChange={(e) => setAddPackagingTypeNo(e.target.value)}
              disabled={saving || availablePackagingTypes.length === 0}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">
                {availablePackagingTypes.length === 0
                  ? t('manager.modules.batchPickingSetup.packages.noMorePackages', {}, 'Alle esker er lagt til')
                  : t('manager.modules.batchPickingSetup.packages.selectPackage', {}, 'Velg eske...')}
              </option>
              {availablePackagingTypes.map((pt) => (
                <option key={pt.packagingTypeNo} value={pt.packagingTypeNo}>
                  {pt.packagingTypeNo} – {pt.description}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving || addPackagingTypeNo === ''}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {t('manager.modules.batchPickingSetup.packages.add', {}, 'Legg til eske')}
            </button>
          </div>
        </div>

        <div className="flex justify-end p-4 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            {t('common.buttons.close', {}, 'Lukk')}
          </button>
        </div>
      </div>
    </div>
  );
};
